import { asArray } from "@/lib/asArray";
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { toErrMsg, UPLOAD_TIMEOUT_MS } from "@/lib/api";
import { uploadGLChunked, CHUNKED_UPLOAD_THRESHOLD } from "@/lib/chunkedUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import FullScreenDialog from "@/components/FullScreenDialog";
import {
  DialogDescription,
} from "@/components/ui/dialog";
import { Upload, Loader2, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { fmtCount } from "@/components/Amount";   // STEP 3 combined · B6b: the one count formatter (Western digits)
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const ACCEPTED = ".csv,.xlsx,.xls,.xlsm";

/**
 * Head-wise GL upload dialog.
 */
export default function HeadwiseGLDialog({ open, onOpenChange, engagementId,
                                            onDone }) {
  const { lang, dict, t } = useI18n();
  const HEADWISE_MAP_FIELDS = [
    { key: "voucher_no",     label: t("headwiseGL.fields.voucherLabel"), hint: t("headwiseGL.fields.voucherHint") },
    { key: "entry_date",     label: t("headwiseGL.fields.entryDate"),    hint: "" },
    { key: "account_code",   label: t("headwiseGL.fields.accountCode"),  hint: "" },
    { key: "account_name",   label: t("headwiseGL.fields.accountName"),  hint: "" },
    { key: "description",    label: t("headwiseGL.fields.description"),  hint: "" },
    { key: "debit",          label: t("headwiseGL.fields.debit"),        hint: "" },
    { key: "credit",         label: t("headwiseGL.fields.credit"),       hint: "" },
    { key: "vendor_customer", label: t("headwiseGL.fields.vendorCustomer"), hint: "" },
  ];
  const [taxonomy, setTaxonomy] = useState({ fs_lines: [] });
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [selectedPlacements, setSelectedPlacements] = useState(new Set());
  const [mode, setMode] = useState("append");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [map, setMap] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Progress state for the async /gl/preview background job (files > 5 MB).
  // Small files still parse inline and never populate this.
  const [previewJob, setPreviewJob] = useState(null);
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/fs/taxonomy").then((r) => setTaxonomy(r.data || { fs_lines: [] }))
      .catch(() => setTaxonomy({ fs_lines: [] }));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedCodes(new Set()); setSelectedPlacements(new Set());
      setMode("append"); setFile(null); setPreview(null); setMap({});
      setSearch(""); setPreviewJob(null);
    }
  }, [open]);

  const placements = useMemo(() => {
    const s = new Set(); asArray(taxonomy.fs_lines).forEach((l) => l.placement && s.add(l.placement));
    return Array.from(s);
  }, [taxonomy]);

  const filteredLines = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return taxonomy.fs_lines || [];
    return asArray(taxonomy.fs_lines).filter((l) =>
      (l.code + " " + l.line + " " + l.placement).toLowerCase().includes(q));
  }, [taxonomy, search]);

  const toggleCode = (code) => {
    const s = new Set(selectedCodes);
    if (s.has(code)) s.delete(code); else s.add(code);
    setSelectedCodes(s);
  };
  const togglePlacement = (p) => {
    const s = new Set(selectedPlacements);
    if (s.has(p)) s.delete(p); else s.add(p);
    setSelectedPlacements(s);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const okExt = ["csv", "xlsx", "xls", "xlsm"].some((x) =>
      f.name.toLowerCase().endsWith("." + x));
    if (!okExt) { toast.error(t("headwiseGL.toastUnsupported")); return; }
    setUploading(true); setFile(f);
    let willPoll = false;
    const applyPreview = (data) => {
      setPreview(data);
      setMap(data.suggested_map || {});
      setShowMap(!((data.suggested_map || {}).voucher_no));
      toast.success(t("headwiseGL.loadedRows").replace("{n}", fmtCount(data.total_rows || 0)));
    };
    const pollJob = (jobId) => {
      willPoll = true;
      setPreviewJob({ id: jobId, status: "queued", progress_pct: 0,
                      message: t("headwiseGL.parsingFile") });
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          setPreviewJob(jr.data);
          if (jr.data.status === "done") {
            applyPreview(jr.data.result || {});
            setPreviewJob(null); setUploading(false);
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, t("headwiseGL.toastPreviewFailed")));
            setPreviewJob(null); setUploading(false);
            setPreview(null); setFile(null);
            return;
          }
          setTimeout(poll, 1200);
        } catch (err) {
          toast.error(toErrMsg(err, t("headwiseGL.toastPreviewFailed")));
          setPreviewJob(null); setUploading(false);
          setPreview(null); setFile(null);
        }
      };
      setTimeout(poll, 400);
    };
    try {
      if (f.size >= CHUNKED_UPLOAD_THRESHOLD) {
        setPreviewJob({ id: "upload", status: "running", progress_pct: 0,
                        message: t("headwiseGL.uploadingChunks").replace("{mb}", (f.size / 1024 / 1024).toFixed(1)) });
        const res = await uploadGLChunked(f, (pct, msg) => {
          setPreviewJob({ id: "upload", status: "running",
                          progress_pct: pct, message: msg });
        });
        if (res && res.job_id) {
          pollJob(res.job_id);
        } else {
          toast.error(t("headwiseGL.toastFinalizeNoJob"));
          setPreviewJob(null); setUploading(false);
          setPreview(null); setFile(null);
        }
      } else {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/gl/preview", fd,
          { headers: { "Content-Type": "multipart/form-data" },
            timeout: UPLOAD_TIMEOUT_MS, _silent: true });
        if (r.data && r.data.job_id) {
          pollJob(r.data.job_id);
        } else {
          applyPreview(r.data);
        }
      }
    } catch (err) {
      toast.error(toErrMsg(err, t("headwiseGL.toastFileParseFailed")));
      setPreview(null); setFile(null);
      setPreviewJob(null);
    } finally {
      if (!willPoll) setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const doCommit = async () => {
    if (!preview) { toast.error(t("headwiseGL.toastFileFirst")); return; }
    if (selectedCodes.size === 0 && selectedPlacements.size === 0) {
      toast.error(t("headwiseGL.toastPickFsHead")); return;
    }
    setCommitting(true);
    try {
      const r = await api.post("/gl/commit-headwise", {
        engagement_id: engagementId,
        column_map: map,
        // Server-side staging (preferred) — falls back to inline rows if the
        // preview endpoint didn't return a staging id.
        preview_id: preview.preview_id || undefined,
        rows: preview.preview_id ? [] : (preview.rows || []),
        source_file: preview.filename || (file?.name || "headwise.xlsx"),
        period: "current",
        mode,
        fs_codes: Array.from(selectedCodes),
        placements: Array.from(selectedPlacements),
      });
      // Backend now runs head-wise commit as a background job.
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Legacy synchronous response.
        const d = r.data;
        const msg = mode === "replace"
          ? t("headwiseGL.toastCompleteReplace").replace("{n}", fmtCount(d.inserted)).replace("{r}", fmtCount(d.replaced_count))
          : t("headwiseGL.toastCompleteAppend").replace("{n}", fmtCount(d.inserted));
        toast.success(msg);
        onOpenChange(false);
        onDone?.(d);
        return;
      }
      const poll = async () => {
        try {
          const jr = await api.get(`/jobs/${jobId}`, { _silent: true });
          if (jr.data.status === "done") {
            const d = jr.data.result || {};
            const msg = mode === "replace"
              ? t("headwiseGL.toastCompleteReplace").replace("{n}", fmtCount(d.inserted || 0)).replace("{r}", fmtCount(d.replaced_count || 0))
              : t("headwiseGL.toastCompleteAppend").replace("{n}", fmtCount(d.inserted || 0));
            toast.success(msg);
            setCommitting(false);
            onOpenChange(false);
            onDone?.(d);
            return;
          }
          if (jr.data.status === "failed") {
            toast.error(jobErrorText(jr.data, lang, t, t("headwiseGL.toastCompleteFailed")));
            setCommitting(false);
            return;
          }
          setTimeout(poll, 1200);
        } catch {
          setCommitting(false);
        }
      };
      setTimeout(poll, 800);
    } catch (err) {
      toast.error(toErrMsg(err, t("headwiseGL.toastCompleteFailed")));
      setCommitting(false);
    }
  };

  return (
    <FullScreenDialog open={open} onOpenChange={onOpenChange}
                      testid="headwise-dialog"
                      title={t("headwiseGL.title")}>
        <DialogDescription className="mb-3">
            {t("headwiseGL.descPre")}<b>{t("headwiseGL.descAppendBold")}</b>{t("headwiseGL.descMid")}<b>{t("headwiseGL.descReplaceBold")}</b>{t("headwiseGL.descPost")}
        </DialogDescription>

        {/* Mode */}
        <div className="rounded-md border bg-slate-50 p-3 dark:bg-slate-900/40">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("headwiseGL.mode")}
          </Label>
          <RadioGroup value={mode} onValueChange={setMode}
                     className="mt-1 flex flex-col gap-2">
            <label className="flex cursor-pointer items-start gap-2"
                   data-testid="headwise-mode-append">
              <RadioGroupItem value="append" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium">{t("headwiseGL.append")}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t("headwiseGL.appendBody")}
                </div>
              </div>
            </label>
            <label className="flex cursor-pointer items-start gap-2"
                   data-testid="headwise-mode-replace">
              <RadioGroupItem value="replace" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium">{t("headwiseGL.replacePerHead")}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t("headwiseGL.replaceBody")}
                </div>
              </div>
            </label>
          </RadioGroup>
        </div>

        {/* FS heads picker */}
        <div className="rounded-md border p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("headwiseGL.fsHeads")}
            </Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={t("headwiseGL.searchPh")}
                   className="h-7 w-56 text-xs"
                   data-testid="headwise-search" />
          </div>
          {/* Placements */}
          <div className="mb-2">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("headwiseGL.groupByPlacement")}
            </div>
            <div className="flex flex-wrap gap-1">
              {placements.map((p) => (
                <label key={p} className={`cursor-pointer rounded-full border px-2 py-0.5 text-[11px] ${
                    selectedPlacements.has(p)
                      ? "border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200"
                      : "bg-white dark:bg-slate-950"
                  }`}
                  data-testid={`headwise-placement-${p.replace(/\s+/g, "-").toLowerCase()}`}>
                  <input type="checkbox" className="me-1 align-middle"
                         checked={selectedPlacements.has(p)}
                         onChange={() => togglePlacement(p)} />
                  {p}
                </label>
              ))}
            </div>
          </div>
          {/* Codes list */}
          <div className="max-h-40 overflow-y-auto rounded border bg-white text-xs dark:bg-slate-950"
               data-testid="headwise-codes">
            {filteredLines.length === 0 && (
              <div className="p-3 text-center text-muted-foreground">
                {t("headwiseGL.noLinesMatch").replace("{q}", search)}
              </div>
            )}
            {filteredLines.map((l) => (
              <label key={l.code}
                     className={`flex cursor-pointer items-center gap-2 border-b px-2 py-1 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-900 ${
                       selectedCodes.has(l.code) ? "bg-emerald-50 dark:bg-emerald-950/40" : ""
                     }`}
                     data-testid={`headwise-code-${l.code}`}>
                <Checkbox checked={selectedCodes.has(l.code)}
                          onCheckedChange={() => toggleCode(l.code)} />
                <span className="font-mono text-[10px] text-emerald-700">{l.code}</span>
                <span className="flex-1">{l.line}</span>
                <span className="text-[10px] text-muted-foreground">{l.placement}</span>
              </label>
            ))}
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            {t("headwiseGL.selectedLine").replace("{codes}", fmtPlural(selectedCodes.size, lang, dict.plurals.codes))}
            {selectedPlacements.size > 0 && t("headwiseGL.selectedPlusPlacements").replace("{placements}", fmtPlural(selectedPlacements.size, lang, dict.plurals.placementGroups))}
          </div>
        </div>

        {/* File upload */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("headwiseGL.glFile")}
          </Label>
          <label className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 p-6 text-sm hover:border-emerald-500 hover:bg-emerald-50/40">
            {preview ? (
              <>
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                <span>
                  <b>{preview.filename || file?.name}</b> — {t("headwiseGL.loadedRows").replace("{n}", fmtCount(preview.total_rows))}
                </span>
                <span className="ms-2 text-[10px] text-muted-foreground">{t("headwiseGL.clickToReplace")}</span>
              </>
            ) : previewJob ? (
              <div className="flex w-full flex-col gap-2 px-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                  <span className="text-xs">
                    {previewJob.message || t("headwiseGL.parsingBg")}
                  </span>
                  <span className="ms-auto text-[11px] font-semibold text-emerald-700">
                    {Number(previewJob.progress_pct || 0)}%
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full bg-emerald-600 transition-all"
                       style={{ width: `${Number(previewJob.progress_pct || 0)}%` }} />
                </div>
              </div>
            ) : uploading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t("headwiseGL.parsing")}</>
            ) : (
              <><Upload className="h-5 w-5" /> {t("headwiseGL.choose")}</>
            )}
            <input ref={fileInputRef} type="file" accept={ACCEPTED}
                   className="hidden" onChange={onFileChange}
                   data-testid="headwise-file-input" disabled={uploading} />
          </label>
        </div>

        {/* Column mapping — expose so voucher can be picked. Auto-opens
            when the parser can't guess a voucher column. */}
        {preview && (
          <div className="rounded-md border p-3">
            <button type="button"
                    onClick={() => setShowMap((v) => !v)}
                    className="flex w-full items-center justify-between text-start"
                    data-testid="headwise-map-toggle">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("headwiseGL.columnMapping")}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {map.voucher_no
                    ? <>{t("headwiseGL.voucherCol")} <b className="text-emerald-700">{map.voucher_no}</b></>
                    : <span className="text-amber-700">{t("headwiseGL.noVoucherMapped")}</span>}
                </div>
              </div>
              {showMap ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {showMap && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {HEADWISE_MAP_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {f.label}
                    </Label>
                    <select
                      value={map[f.key] || ""}
                      onChange={(e) => setMap({ ...map, [f.key]: e.target.value })}
                      className="mt-0.5 w-full rounded-md border bg-white px-2 py-1 text-xs dark:bg-slate-950"
                      data-testid={`headwise-map-${f.key}`}
                    >
                      <option value="">{t("headwiseGL.notMapped")}</option>
                      {asArray(preview.columns).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {f.hint && (
                      <div className="mt-0.5 text-[10px] text-muted-foreground">{f.hint}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="headwise-cancel">{t("headwiseGL.cancel")}</Button>
          <Button onClick={doCommit}
                  disabled={committing || !preview
                             || (selectedCodes.size === 0 && selectedPlacements.size === 0)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="headwise-submit">
            {committing ? <><Loader2 className="me-1 h-3 w-3 animate-spin" /> {t("headwiseGL.uploading")}</> :
             (mode === "replace" ? t("headwiseGL.uploadReplace") : t("headwiseGL.uploadAppend"))}
          </Button>
        </div>
    </FullScreenDialog>
  );
}
